import { useGetRunCostQuery } from '@/services/api';
import { ErrorState } from './ErrorState';
import { SkeletonGridRow } from './Skeleton';

export function CostTab({
  runId,
  refreshIntervalMs = 0
}: {
  runId: string;
  refreshIntervalMs?: number;
}): JSX.Element {
  const resp = useGetRunCostQuery(runId, {
    pollingInterval: refreshIntervalMs,
    skipPollingIfUnfocused: true
  });

  if (resp.isLoading) {
    return (
      <section className="grid grid-cols-2 gap-3" data-testid="cost-tab-loading">
        <SkeletonCard />
        <SkeletonCard />
      </section>
    );
  }
  if (resp.isError || !resp.data) {
    return (
      <section data-testid="cost-tab-error">
        <ErrorState
          title="Couldn't load cost data"
          body="The cost summary couldn't be loaded right now. Other tabs above are unaffected."
          onRetry={() => void resp.refetch()}
        />
      </section>
    );
  }

  const cost = resp.data;

  return (
    <section data-testid="cost-tab">
      <div
        className="flex items-center gap-2.5 px-3.5 py-2.5 mb-4 rounded-md border border-warn/25 bg-warn/10 text-[11.5px] text-warn"
        role="status"
        data-testid="cost-estimate-note"
      >
        <span>ⓘ</span>
        <span>
          <strong>Cost is approximate</strong> — calculated from the tokens this run actually
          used, priced against the model list at run time. The final billed amount may differ
          slightly.
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SummaryCard
          label="Total tokens"
          value={cost.total_tokens.toLocaleString()}
          testId="cost-total-tokens"
        />
        <SummaryCard
          label="Total cost (approx.)"
          value={`$${cost.total_cost_usd_estimate.toFixed(3)}`}
          sub="calculated from tokens used"
          accent
          testId="cost-total-usd"
        />
      </div>
    </section>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  accent = false,
  testId
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  testId: string;
}): JSX.Element {
  return (
    <div
      className="bg-surface-alt border border-border-strong rounded-lg px-[18px] py-4"
      data-testid={testId}
    >
      <div className="font-mono text-[10px] text-text-muted uppercase tracking-[0.08em] mb-2">{label}</div>
      <div
        className={`font-mono text-[22px] font-semibold tabular-nums ${accent ? 'text-accent' : 'text-text'}`}
      >
        {value}
      </div>
      {sub ? <div className="text-[11.5px] text-text-muted mt-1.5">{sub}</div> : null}
    </div>
  );
}

function SkeletonCard(): JSX.Element {
  return (
    <div className="bg-panel border border-border rounded-lg p-6">
      <SkeletonGridRow cols={1} />
      <SkeletonGridRow cols={1} />
    </div>
  );
}
